import { Route, Routes, useLocation } from "react-router-dom";

import YourName from "src/pages/YourName";
import ContactInformation from "src/pages/ContactInformation";
import Address from "src/pages/Address";
import ContractChange from "src/pages/ContractChange";
import { DEMO_ROUTES, ONBOARDING_ROUTES, ROUTES } from "src/consts/routes";
import { Box, Container, Grid, Stack } from "@mui/material";
import OrderTariffProgressBar from "./components/OrderTariffProgressBar/OrderTariffProgressBar";
import { ONBOARDING_PATHS, TARIFF_AND_ORDER_PATHS } from "./consts/paths";
import ContractStartDate from "./pages/ContractStartDate";
import PaymentDetails from "./pages/PaymentDetails";
import TechnicalDetails from "./pages/TechnicalDetails";
import CurrentSupplier from "./pages/CurrentSupplier";
import SmartMeter from "./pages/SmartMeter";
import ContractSummary from "./pages/ContractSummary/ContractSummary";
import Conclusion from "./pages/Conclusion";
import Introduction from "./pages/Introduction/Introduction";
import RainbowFooter from "./components/Footer/RainbowFooter";
import Wallbox from "./pages/Wallbox";
import PvSetup from "./pages/PvSetup";
import AnnualGridSupply from "./pages/AnnualGridSupply";
import CalculatedSavings from "./pages/CalculatedSavings/CalculatedSavings";
import GlobalContext from "./components/GlobalContext/GlobalContext";
import BatterySetup from "./pages/BatterySetup";
import FooterLinks from "./components/Footer/FooterLinks";
import ErrorPage from "./pages/ErrorPage";
import BrandItemThemeChanger from "./components/BrandIconThemeChanger/BrandIconThemeChanger";
import Demo from "./pages/Demo";
import { useContext, useEffect } from "react";
import { ThemeContext } from "./components/ThemeUsage/ThemeUsage";
import { ThemeValues } from "./components/ThemeUsage/theme";
import IntroductionSMA from "./pages/IntroductionSMA/IntroductionSMA";
import useLeadTransfer from "./hooks/useLeadTransfer";
import ConnectedDevices from "./pages/Onboarding/ConnectedDevices";
import System from "./pages/Onboarding/System";
import OnboardingError from "./pages/Onboarding/OnboardingError";
import TarifSelection from "./pages/Tarif/TarifSelection";
import SmartCharging from "./components/SmartCharging";
import SavingsCalculatorProgressBar from "./pages/Tarif/components/SavingsCalculatorProgressBar";
import SiteAuthenticationContainer from "./components/MfeContainers/SiteAuthentication";
import SelfOnboardingContainer from "./components/MfeContainers/SelfOnboarding";
import SelfOnboardingAccessContainer from "./components/MfeContainers/SelfOnboardingAccess";
import IntroductionLB from "./pages/IntroductionLB/IntroductionLB";
import { SMART_CHARGING_PATHS } from "./consts/Routes/SmartCharging";
import {
  SELF_ONBOARDING_ACCESS_ROUTES,
  SELF_ONBOARDING_ROUTES,
} from "./consts/Routes/SelfOnboarding";
import { SITE_AUTHENTICATION_ROUTES } from "./consts/Routes/SiteAuthentication";
import ContractSummarySMA from "./pages/ContractSummarySMA";
import { EV_ROUTES } from "./consts/Routes/Ev";
import EvContainer from "./components/MfeContainers/Ev";
import { EV_EXISTING_ROUTES } from "./consts/Routes/EvExisting";
import EvExistingContainer from "./components/MfeContainers/EvExisting";

const App = () => {
  const { pathname } = useLocation();
  const { isLoading } = useLeadTransfer();
  const themeName = useContext(ThemeContext);
  const showB2Cdemo = process.env.REACT_APP_BUILD_ENV === "DEV";
  const showOnboardingSteps = process.env.REACT_APP_BUILD_ENV === "DEV";
  const isUserSelectEnabled = themeName !== ThemeValues.lichtblick;

  useEffect(() => {
    const script = document.createElement("script");
    const matomoContainerUrl = process.env.REACT_APP_MATOMO_CONTAINER_URL || "";
    script.src = matomoContainerUrl;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (pathname === ROUTES.INTRODUCTION) {
    if (themeName === ThemeValues.SMA) {
      return <IntroductionSMA isLoading={isLoading} />;
    }
    if (themeName === ThemeValues.lichtblick) {
      return <IntroductionLB isLoading={isLoading} />;
    }
    return <Introduction isLoading={isLoading} />;
  }

  if (pathname.includes(DEMO_ROUTES.DEMO)) {
    return (
      <Routes>
        {showB2Cdemo && (
          <Route path={DEMO_ROUTES.DEMO}>
            <Route path=":uuid" element={<Demo />} />
          </Route>
        )}
      </Routes>
    );
  }

  if (SMART_CHARGING_PATHS.includes(pathname)) {
    return <SmartCharging />;
  }

  if (EV_ROUTES.includes(pathname)) {
    return <EvContainer />;
  }

  if (EV_EXISTING_ROUTES.includes(pathname)) {
    return <EvExistingContainer />;
  }

  if (SITE_AUTHENTICATION_ROUTES.includes(pathname)) {
    return <SiteAuthenticationContainer />;
  }

  if (SELF_ONBOARDING_ROUTES.includes(pathname)) {
    return <SelfOnboardingContainer />;
  }

  if (SELF_ONBOARDING_ACCESS_ROUTES.includes(pathname)) {
    return <SelfOnboardingAccessContainer />;
  }

  if (ONBOARDING_PATHS.includes(pathname)) {
    return (
      <Routes>
        {showOnboardingSteps && (
          <>
            <Route
              path={ONBOARDING_ROUTES.ONBOARDING_SYSTEM}
              element={<System />}
            />
            <Route
              path={ONBOARDING_ROUTES.ONBOARDING_CONNECTED_DEVICES}
              element={<ConnectedDevices />}
            />
            <Route
              path={ONBOARDING_ROUTES.ONBOARDING_ERROR_RETRY}
              element={<OnboardingError />}
            />
          </>
        )}
      </Routes>
    );
  }

  return (
    <Stack
      sx={(theme) => ({
        minHeight: "100vh",
        background: theme.palette.background.default,
        userSelect: isUserSelectEnabled ? undefined : "none",
      })}
    >
      <Container
        sx={{
          marginTop: { xs: 0, sm: 12 },
          flexGrow: 1,
          justifyContent: "space-between",
          alignContent: "space-between",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={4}>
            <Stack gap="50px">
              <Box
                sx={{
                  display: { xs: "none", sm: "flex" },
                }}
              >
                <BrandItemThemeChanger />
              </Box>
              {TARIFF_AND_ORDER_PATHS.includes(pathname) && (
                <>
                  <SavingsCalculatorProgressBar />
                  <OrderTariffProgressBar />
                </>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12} sm={8} sx={{ paddingLeft: { sm: 5, md: 0 } }}>
            <Routes>
              <Route path="/" element={<GlobalContext />}>
                <Route path={ROUTES.PV_SETUP} element={<PvSetup />} />
                <Route path={ROUTES.BATTERY_SETUP} element={<BatterySetup />} />
                <Route path={ROUTES.WALLBOX} element={<Wallbox />} />
                <Route
                  path={ROUTES.ANNUAL_GRID_SUPPLY}
                  element={<AnnualGridSupply />}
                />
                <Route path={ROUTES.SMART_METER} element={<SmartMeter />} />
                <Route
                  path={ROUTES.CALCULATED_SAVINGS}
                  element={<CalculatedSavings />}
                />
                <Route path={ROUTES.TARIF} element={<TarifSelection />} />

                <Route path={ROUTES.YOUR_NAME} element={<YourName />} />
                <Route
                  path={ROUTES.CONTACT_INFORMATION}
                  element={<ContactInformation />}
                />
                <Route path={ROUTES.ADDRESS} element={<Address />} />
                <Route
                  path={ROUTES.TECHNICAL_DETAILS}
                  element={<TechnicalDetails />}
                />
                <Route
                  path={ROUTES.CONTRACT_CHANGE}
                  element={<ContractChange />}
                />
                <Route
                  path={ROUTES.CURRENT_SUPPLIER}
                  element={<CurrentSupplier />}
                />
                <Route
                  path={ROUTES.DATE_OF_CONTRACT_START}
                  element={<ContractStartDate />}
                />

                <Route
                  path={ROUTES.PAYMENT_DETAILS}
                  element={<PaymentDetails />}
                />
                <Route
                  path={ROUTES.CONTRACT_SUMMARY}
                  element={<ContractSummary />}
                />

                <Route
                  path={ROUTES.CONTRACT_SUMMARY_SMA}
                  element={<ContractSummarySMA />}
                />

                <Route path={ROUTES.CONCLUSION} element={<Conclusion />} />
                <Route path={ROUTES.ERROR} element={<ErrorPage />} />
              </Route>
            </Routes>
          </Grid>
        </Grid>
        <FooterLinks />
      </Container>
      <RainbowFooter />
    </Stack>
  );
};

export default App;
